/* jshint esversion: 6 */
import { Controller } from '@hotwired/stimulus';

// Connects to data-controller="room-messages"
export default class extends Controller {
  static targets = ['messagesContainer', 'messageForm', 'loadingIndicator', 'bodyInput', 'draftButton', 'aiTyping'];
  static values = {
    roomId: Number,
    oldestMessageId: Number,
    hasMore: Boolean,
    loadMoreUrl: String,
    timeout: { type: Number, default: 60000 }
  };

  connect() {
    this.scrollToBottom();
    this.initializeFormValidation();
    this.initializeScrollListener();
    this.observeIncomingMessages();
    this.isLoading = false;

    // Show indicator if all messages are already loaded on initial page load
    if (!this.hasMoreValue && this.oldestMessageIdValue) {
      this.showFirstMessageIndicator();
    }
  }

  disconnect() {
    if (this.scrollHandler) {
      this.messagesContainerTarget.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.incomingObserver) {
      this.incomingObserver.disconnect();
    }
    if (this.aiTypingTimer) {
      clearTimeout(this.aiTypingTimer);
    }
  }

  // #2619: Action Cable / Turbo Stream で末尾へ追加された新着メッセージ (相手・AI)
  // を監視し、自動で最下部へスクロールする。AI「回答生成中」インジケータが
  // 追加されたら、応答が来ないケースの保険としてタイムアウトで自動除去する。
  observeIncomingMessages() {
    const container = this.messagesContainerTarget;
    if (!container) return;

    this.incomingObserver = new MutationObserver((mutations) => {
      let appended = false;
      const addedThisBatch = [];
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          appended = true;
          addedThisBatch.push(node);
          if (node.id === 'ai-typing-indicator') {
            this.scheduleAiTypingTimeout();
          }
        });
      });

      // 生成中インジケータの除去は「別バッチで届いた実メッセージ」を合図にする
      // (broadcast_remove の保険)。同一バッチで自分の送信メッセージとインジケータが
      // 一緒に append されるため、そのバッチでは消さない。
      const indicatorAdded = addedThisBatch.some((n) => n.id === 'ai-typing-indicator');
      const messageAdded = addedThisBatch.some(
        (n) => n.id !== 'ai-typing-indicator' && n.classList && n.classList.contains('message')
      );
      if (messageAdded && !indicatorAdded) {
        this.removeAiTyping();
      }

      if (appended) this.scrollToBottom();
    });

    this.incomingObserver.observe(container, { childList: true });
  }

  scheduleAiTypingTimeout() {
    if (this.aiTypingTimer) clearTimeout(this.aiTypingTimer);
    this.aiTypingTimer = setTimeout(() => this.removeAiTyping(), this.timeoutValue);
  }

  removeAiTyping() {
    if (this.aiTypingTimer) {
      clearTimeout(this.aiTypingTimer);
      this.aiTypingTimer = null;
    }
    const indicator = this.messagesContainerTarget?.querySelector('#ai-typing-indicator');
    if (indicator) indicator.remove();
  }

  // #2229: 管理者がボタンを押すと LLM 下書きを生成し、返信欄に差し込む。
  // 送信はしない(管理者が確認・編集してから通常の送信フォームで送る)。
  async generateDraft(event) {
    const button = event.currentTarget;
    const url = button.dataset.url;
    if (!url) return;

    const original = button.textContent;
    button.disabled = true;
    button.textContent = '生成中…';

    try {
      const token = document.querySelector('meta[name="csrf-token"]')?.content;
      const response = await fetch(url, {
        method: 'POST',
        headers: {
          'X-CSRF-Token': token,
          Accept: 'application/json'
        }
      });
      const data = await response.json();

      if (response.ok && data.body) {
        if (this.hasBodyInputTarget) {
          this.bodyInputTarget.value = data.body;
          this.bodyInputTarget.focus();
        }
      } else {
        window.alert(data.error || '下書きの生成に失敗しました。');
      }
    } catch (e) {
      window.alert('下書きの生成に失敗しました。');
    } finally {
      button.disabled = false;
      button.textContent = original;
    }
  }

  scrollToBottom() {
    const container = this.messagesContainerTarget;

    if (container) {
      container.scrollTop = container.scrollHeight;
    }
  }

  // Called when new messages are added via AJAX
  messageAdded() {
    this.scrollToBottom();
  }

  initializeFormValidation() {
    if (!this.hasMessageFormTarget) return;
    const form = this.messageFormTarget;

    if (form) {
      form.addEventListener('submit', this.validateForm.bind(this));
      // Turbo submits the form; on success reset the form and scroll to the
      // newly appended message, on failure recover from a stale CSRF token
      // (e.g. long-open tab whose session token rotated) by reloading so a
      // fresh token is issued and the user can resend instead of failing
      // silently. See #1756.
      form.addEventListener('turbo:submit-end', this.handleSubmitEnd.bind(this));
    }
  }

  handleSubmitEnd(event) {
    const { success, fetchResponse } = event.detail;

    if (success) {
      this.resetForm();
      this.scrollToBottom();
      return;
    }

    const status = fetchResponse && fetchResponse.response && fetchResponse.response.status;

    // #3182: AI 質問の1日の上限 (#2679) に達したときの 429。モーダルから送った
    // 場合にリロードするとモーダルごと閉じてしまうため、理由をその場に出す。
    if (status === 429) {
      this.showQuotaNotice(fetchResponse);
      return;
    }

    if (status === 401 || status === 404 || status === 422) {
      window.location.reload();
    }
  }

  async showQuotaNotice(fetchResponse) {
    let message = '';
    try {
      message = (await fetchResponse.responseText || '').trim();
    } catch (e) {
      message = '';
    }
    if (!message) return;

    const existing = this.element.querySelector('[data-room-messages-quota]');
    if (existing) existing.remove();

    const notice = document.createElement('div');
    notice.className = 'alert alert-warning mt-2 mb-0';
    notice.setAttribute('data-room-messages-quota', '');
    notice.textContent = message;
    this.messageFormTarget.insertAdjacentElement('beforebegin', notice);
  }

  resetForm() {
    const form = this.messageFormTarget;
    form.reset();
    form.classList.remove('was-validated');

    const attachmentPreview = this.element.querySelector('[data-attachment-preview-target="preview"]');
    if (attachmentPreview) {
      attachmentPreview.classList.add('d-none');
      attachmentPreview.innerHTML = '';
    }
  }

  initializeScrollListener() {
    const container = this.messagesContainerTarget;

    if (!container) return;

    this.scrollHandler = this.handleScroll.bind(this);
    container.addEventListener('scroll', this.scrollHandler);
  }

  handleScroll() {
    const container = this.messagesContainerTarget;

    if (!container) return;

    // Check if scrolled to top (within 50px threshold)
    if (container.scrollTop <= 50 && !this.isLoading && this.hasMoreValue) {
      this.loadMoreMessages();
    }
  }

  async loadMoreMessages() {
    if (this.isLoading || !this.hasMoreValue || !this.oldestMessageIdValue) return;

    this.isLoading = true;
    this.showLoadingIndicator();

    const container = this.messagesContainerTarget;
    const previousScrollHeight = container.scrollHeight;

    try {
      const url = `${this.loadMoreUrlValue}?before_id=${this.oldestMessageIdValue}`;
      const response = await fetch(url, {
        headers: {
          'Accept': 'application/json',
          'X-Requested-With': 'XMLHttpRequest'
        }
      });

      if (!response.ok) throw new Error('Failed to load messages');

      const data = await response.json();

      if (data.messages && data.messages.length > 0) {
        this.prependMessages(data.messages);

        // Update oldest message ID for next load
        this.oldestMessageIdValue = data.messages[data.messages.length - 1].id;

        // Maintain scroll position
        const newScrollHeight = container.scrollHeight;

        container.scrollTop = newScrollHeight - previousScrollHeight;
      }

      this.hasMoreValue = data.has_more;

      // Show "最初のメッセージ" indicator when all messages are loaded
      if (!data.has_more) {
        this.showFirstMessageIndicator();
      }
    } catch (error) {
      console.error('Error loading messages:', error);
    } finally {
      this.isLoading = false;
      this.hideLoadingIndicator();
    }
  }

  prependMessages(messages) {
    const container = this.messagesContainerTarget;
    const firstMessage = container.firstElementChild;

    messages.forEach(message => {
      const messageElement = this.createMessageElement(message);

      if (firstMessage) {
        container.insertBefore(messageElement, firstMessage);
      } else {
        container.appendChild(messageElement);
      }
    });
  }

  createMessageElement(message) {
    const div = document.createElement('div');

    div.className = 'message mb-3';
    div.id = `message-${message.id}`;

    const avatarStyle = this.getAvatarStyle(message.sender.name);
    const truncatedName = message.sender.name.length > 10
      ? message.sender.name.substring(0, 10) + '...'
      : message.sender.name;

    if (message.is_current_user) {
      div.innerHTML = `
        <div class="d-flex justify-content-end">
          <div class="message-bubble message-bubble-sent p-2 rounded" style="max-width: 70%;">
            ${message.subject ? `<strong>${this.escapeHtml(message.subject)}</strong><br>` : ''}
            ${this.formatMessageBody(message.body)}
            ${this.attachmentHtml(message)}
            <div class="message-meta">
              <small style="color: #666;">${message.created_at}</small>
            </div>
          </div>
          <div class="avatar d-flex flex-column align-items-center ms-2">
            <div class="avatar-circle text-white d-flex align-items-center justify-content-center ${avatarStyle}"
                 style="width: 36px; height: 36px; border-radius: 50%; font-size: 14px; font-weight: 500;">
              ${message.sender.initials}
            </div>
            <small class="text-muted mt-1">${this.escapeHtml(truncatedName)}</small>
          </div>
        </div>
      `;
    } else {
      div.innerHTML = `
        <div class="d-flex align-items-start">
          <div class="avatar d-flex flex-column align-items-center me-2">
            <div class="avatar-circle text-white d-flex align-items-center justify-content-center ${avatarStyle}"
                 style="width: 36px; height: 36px; border-radius: 50%; font-size: 14px; font-weight: 500;">
              ${message.sender.initials}
            </div>
            <small class="text-muted mt-1">${this.escapeHtml(truncatedName)}</small>
          </div>
          <div class="message-bubble message-bubble-received p-2 rounded" style="max-width: 70%;">
            <div class="message-sender">
              <strong class="text-primary">${this.escapeHtml(message.sender.name)}</strong>
            </div>
            ${message.subject ? `<strong>${this.escapeHtml(message.subject)}</strong><br>` : ''}
            ${this.formatMessageBody(message.body)}
            ${this.attachmentHtml(message)}
            <div class="message-meta">
              <small style="color: #666;">${message.created_at}</small>
            </div>
          </div>
        </div>
      `;
    }

    return div;
  }

  attachmentHtml(message) {
    const a = message.attachment;

    if (!a) return '';

    const media = a.is_image
      ? `<a href="${a.preview_url}" target="_blank" rel="noopener" class="d-inline-block">
           <img src="${a.preview_url}" alt="${this.escapeHtml(a.filename)}" class="dm-attachment-thumbnail rounded" />
         </a>`
      : `<div class="dm-attachment-file d-flex align-items-center p-2 rounded">
           <span class="ms-2 text-truncate">${this.escapeHtml(a.filename)}</span>
         </div>`;

    return `
      <div class="dm-attachment mt-2" id="attachment-${message.id}">
        ${media}
        <div class="dm-attachment-actions mt-1">
          <a href="${a.url}" class="btn btn-sm btn-outline-secondary">ダウンロード</a>
          <a href="${a.delete_url}" class="btn btn-sm btn-outline-danger ms-1"
             data-turbo="true" data-turbo-method="delete" rel="nofollow"
             data-turbo-confirm="この添付ファイルを削除しますか？">削除</a>
        </div>
      </div>
    `;
  }

  getAvatarStyle(name) {
    const styles = [
      'bg-primary', 'bg-success', 'bg-info', 'bg-warning', 'bg-danger', 'bg-secondary'
    ];
    const hash = name.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);

    return styles[hash % styles.length];
  }

  escapeHtml(text) {
    const div = document.createElement('div');

    div.textContent = text;

    return div.innerHTML;
  }

  formatMessageBody(body) {
    // Simple URL detection and linkification
    const urlRegex = /(https?:\/\/[^\s<]+)/g;
    const escaped = this.escapeHtml(body);

    return escaped.replace(urlRegex, '<a href="$1" target="_blank" rel="noopener noreferrer">$1</a>')
      .replace(/\n/g, '<br>');
  }

  showLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.remove('d-none');
    }
  }

  hideLoadingIndicator() {
    if (this.hasLoadingIndicatorTarget) {
      this.loadingIndicatorTarget.classList.add('d-none');
    }
  }

  showFirstMessageIndicator() {
    // Check if indicator already exists
    if (this.messagesContainerTarget.querySelector('.first-message-indicator')) {
      return;
    }

    const indicator = document.createElement('div');

    indicator.className = 'first-message-indicator text-center my-3';
    indicator.innerHTML = `
      <span class="px-3 py-2 rounded" style="background-color: #e9ecef; color: #6c757d; font-size: 0.875rem;">
        最初のメッセージ
      </span>
    `;

    // Insert at the beginning of the messages container
    const container = this.messagesContainerTarget;

    container.insertBefore(indicator, container.firstChild);
  }

  validateForm(event) {
    const form = event.target;

    if (form.checkValidity() === false) {
      event.preventDefault();
      event.stopPropagation();
    }
    form.classList.add('was-validated');
  }

  // Action for manual scroll to bottom (if needed)
  scrollToBottomAction() {
    this.scrollToBottom();
  }
}